// src/TickerMobile.js
import React from "react";
import { toPersianDigits } from "../../utils/toPersianDigits";
import { getTickerDuration } from "../../utils/tickerMotion";
import { useTickerDrag } from "./useTickerDrag";
import TickerMobileSourceIcons from "./TickerMobileSourceIcons";
import "./TickerMobile.css";

const TITLE_CHUNK_SIZE = 26;

const renderStableTitle = (title) => {
  const normalizedTitle = toPersianDigits(title);
  const words = normalizedTitle.split(/(\s+)/);
  const chunks = [];
  let currentChunk = "";

  words.forEach((word) => {
    const nextChunk = `${currentChunk}${word}`;

    if (currentChunk && Array.from(nextChunk).length > TITLE_CHUNK_SIZE) {
      chunks.push(currentChunk);
      currentChunk = word;
    } else {
      currentChunk = nextChunk;
    }
  });

  if (currentChunk) {
    chunks.push(currentChunk);
  }

  return chunks.map((chunk, index) => (
    <span className="ticker-mobile__title-part" key={`${index}-${chunk}`}>
      {chunk}
    </span>
  ));
};

const getTickerItemKey = (item, index) =>
  item?.id ?? item?.link ?? `${index}-${item?.title || ""}`;

const handleTickerItemKeyDown = (event, item, onItemClick) => {
  if (!onItemClick) return;
  if (event.key !== "Enter" && event.key !== " ") return;

  event.preventDefault();
  onItemClick(item, event);
};

const TickerMobile = ({
  showSourceIcons,
  activeSources = [],
  favicons = {},
  randomNews = [],
  paused = false,
  setPaused,
  onItemClick,
  isMobileDevice,
  isHidden = false,
}) => {
  const containerRef = React.useRef(null);
  const groupRef = React.useRef(null);
  const contentRef = React.useRef(null);

  const [tickerLayout, setTickerLayout] = React.useState({
    copies: 6,
    distance: 0,
  });

  const tickerSignature = React.useMemo(
    () =>
      randomNews
        .map((item, index) => getTickerItemKey(item, index))
        .join("|"),
    [randomNews]
  );

  React.useLayoutEffect(() => {
    let isActive = true;

    const updateCopies = () => {
      if (!isActive) return;

      const containerWidth = containerRef.current?.offsetWidth || 0;
      const groupWidth = groupRef.current?.scrollWidth || 0;

      if (!containerWidth || !groupWidth) return;

      const measuredDistance = Math.ceil(groupWidth);
      const neededCopies = Math.min(
        16,
        Math.max(4, Math.ceil(containerWidth / measuredDistance) + 2)
      );

      setTickerLayout((current) =>
        current.copies === neededCopies &&
        current.distance === measuredDistance
          ? current
          : { copies: neededCopies, distance: measuredDistance }
      );
    };

    updateCopies();

    const rafId =
      typeof requestAnimationFrame === "function"
        ? requestAnimationFrame(updateCopies)
        : null;

    if (typeof document !== "undefined" && document.fonts?.ready) {
      document.fonts.ready.then(updateCopies).catch(() => {});
    }

    if (typeof ResizeObserver === "undefined") {
      if (typeof window !== "undefined") {
        window.addEventListener("resize", updateCopies);
      }

      return () => {
        isActive = false;

        if (rafId !== null && typeof cancelAnimationFrame === "function") {
          cancelAnimationFrame(rafId);
        }

        if (typeof window !== "undefined") {
          window.removeEventListener("resize", updateCopies);
        }
      };
    }

    const observer = new ResizeObserver(updateCopies);

    if (containerRef.current) observer.observe(containerRef.current);
    if (groupRef.current) observer.observe(groupRef.current);

    return () => {
      isActive = false;

      if (rafId !== null && typeof cancelAnimationFrame === "function") {
        cancelAnimationFrame(rafId);
      }

      observer.disconnect();
    };
  }, [showSourceIcons, tickerSignature]);

  useTickerDrag({
    containerRef,
    contentRef,
    paused,
    enabled: Boolean(isMobileDevice) && !showSourceIcons && !isHidden,
  });

  if (showSourceIcons) {
    return (
      <TickerMobileSourceIcons
        activeSources={activeSources}
        favicons={favicons}
      />
    );
  }

  const tickerGroups = Array.from(
    { length: tickerLayout.copies },
    (_, copyIndex) => copyIndex
  );

  return (
    <div
      ref={containerRef}
      className={`ticker-mobile${isHidden ? " ticker-mobile--hidden" : ""}`}
      onMouseEnter={() => {
        if (!isMobileDevice) setPaused?.(true);
      }}
      onMouseLeave={() => {
        if (!isMobileDevice) setPaused?.(false);
      }}
    >
      <div
        ref={contentRef}
        className={`ticker-mobile__content ${
          paused ? "ticker-mobile__content--paused" : ""
        }`}
        style={{
          "--ticker-mobile-distance": `${tickerLayout.distance}px`,
          "--ticker-mobile-duration": getTickerDuration(
            tickerLayout.distance
          ),
        }}
      >
        {tickerGroups.map((copyIndex) => (
          <div
            key={copyIndex}
            ref={copyIndex === 0 ? groupRef : null}
            className="ticker-mobile__group"
            aria-hidden={copyIndex > 0}
          >
            {randomNews.map((item, itemIndex) => (
              <span
                key={`${copyIndex}-${getTickerItemKey(item, itemIndex)}`}
                className="ticker-mobile__item"
                onClick={(event) => onItemClick?.(item, event)}
                onKeyDown={(event) =>
                  handleTickerItemKeyDown(event, item, onItemClick)
                }
                role="button"
                tabIndex={copyIndex === 0 ? 0 : -1}
              >
                <span className="ticker-mobile__title">
                  {renderStableTitle(item?.title)}
                </span>
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default TickerMobile;
